class CartNotification extends HTMLElement {
  constructor() {
    super();

    this.notification = document.getElementById('cart-notification');
    this.header = document.querySelector('sticky-header');
    this.onBodyClick = this.handleBodyClick.bind(this);

    // Auto-close: close the notification after this many ms unless the user scrolls inside it
    this.autoCloseDelay = 5000;
    this.autoCloseTimer = null;
    this.scrollLockTimer = null;
    this.onUserScroll = this.cancelAutoClose.bind(this);

    this.notification.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelectorAll('button[type="button"]').forEach((closeButton) =>
      closeButton.addEventListener('click', this.close.bind(this))
    );
    this.notification.addEventListener('click', (evt) => {
      if (this.notification.classList.contains('active') && evt.target === this.notification) {
        this.close();
      }
    });

    // Any scroll inside the notification (including nested scroll areas like the
    // product collection) cancels the auto-close. `scroll` doesn't bubble, so we
    // listen in the capture phase; wheel/touchmove catch scroll intent even when
    // the content isn't tall enough to actually scroll.
    this.notification.addEventListener('scroll', this.onUserScroll, { capture: true, passive: true });
    this.notification.addEventListener('wheel', this.onUserScroll, { passive: true });
    this.notification.addEventListener('touchmove', this.onUserScroll, { passive: true });
  }

  open() {
    this.notification.classList.add('animate', 'active');

    this.notification.addEventListener(
      'transitionend',
      () => {
        this.notification.focus();
        trapFocus(this.notification);
      },
      { once: true }
    );

    document.body.addEventListener('click', this.onBodyClick);
    this.dispatchCartViewEvent();

    clearTimeout(this.scrollLockTimer);
    this.scrollLockTimer = setTimeout(() => {
      document.body.classList.add('overflow-hidden');
    }, 350);

    this.startAutoClose();
  }

  startAutoClose() {
    this.cancelAutoClose();
    this.autoCloseTimer = setTimeout(() => {
      this.autoCloseTimer = null;
      if (this.notification.classList.contains('active')) this.close();
    }, this.autoCloseDelay);
  }

  cancelAutoClose() {
    if (this.autoCloseTimer) {
      clearTimeout(this.autoCloseTimer);
      this.autoCloseTimer = null;
    }
  }

  // The notification's outer element is server-rendered once at page load, so
  // its `cart` Liquid object reflects the pre-add state. The morphed children
  // (inserted from the /cart/add.js sections response in renderContents) are
  // post-add, but they don't expose the full cart shape we need for the event
  // payload. So we keep an explicit /cart.json fetch on open. Migrating to the
  // factory + filter would require re-rendering the notification element
  // itself in sections, which is out of scope for this PR.
  async dispatchCartViewEvent() {
    const { CartViewEvent } = window.StandardEvents || {};
    if (!CartViewEvent) return;

    try {
      const response = await fetch(`${routes.cart_url}.json`);
      const cart = await response.json();
      if (!cart?.currency) return;

      this.dispatchEvent(
        new CartViewEvent({
          context: 'dialog',
          cart: CartViewEvent.createCartFromAjaxResponse(cart),
        })
      );
    } catch (e) {
      // cart:view is informational; swallow fetch errors
    }
  }

  close() {
    this.cancelAutoClose();
    clearTimeout(this.scrollLockTimer);

    this.notification.classList.remove('active');
    document.body.removeEventListener('click', this.onBodyClick);
    // Release the page scroll lock that open() applied
    document.body.classList.remove('overflow-hidden');

    removeTrapFocus(this.activeElement);
  }

  renderContents(parsedState) {
    this.cartItemKey = parsedState.key;
    this.getSectionsToRender().forEach((section) => {
      document.getElementById(section.id).innerHTML = this.getSectionInnerHTML(
        parsedState.sections[section.id],
        section.selector
      );
    });

    if (this.header) this.header.reveal();
    this.open();
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-notification-product',
        selector: `[id="cart-notification-product-${this.cartItemKey}"]`,
      },
      {
        id: 'cart-notification-button',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  handleBodyClick(evt) {
    const target = evt.target;
    if (target !== this.notification && !target.closest('cart-notification')) {
      const disclosure = target.closest('details-disclosure, header-menu');
      this.activeElement = disclosure ? disclosure.querySelector('summary') : null;
      this.close();
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-notification', CartNotification);
